import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { adminKeys } from "@/lib/query";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { formatMoney } from "@/lib/format";

const STORAGE_KEY = "aflt-admin-activity-last-seen";

type AdminActivityEvent = {
  id: string;
  type: "retreat-signup" | "shop-order";
  createdAt: string;
  initials: string;
  name: string;
  email: string;
  label: string;
  amountCents: number;
  href: string;
};

type Toast = AdminActivityEvent & { toastId: string };

function initialBaseline(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return stored;
    const now = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, now);
    return now;
  } catch {
    return new Date().toISOString();
  }
}

function toastText(event: AdminActivityEvent): string {
  if (event.type === "retreat-signup") return `${event.initials} signed up for ${event.label}`;
  return `${event.initials} placed a shop order`;
}

export default function AdminActivityToasts() {
  const queryClient = useQueryClient();
  const baselineRef = useRef(initialBaseline());
  const seenRef = useRef(new Set<string>());
  const [toasts, setToasts] = useState<Toast[]>([]);

  const activityQuery = useQuery({
    queryKey: adminKeys.activity(),
    queryFn: () =>
      api.get<{ events: AdminActivityEvent[] }>(
        `/api/admin/activity?since=${encodeURIComponent(baselineRef.current)}`,
      ),
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });

  const newEvents = useMemo(() => {
    const baselineTime = new Date(baselineRef.current).getTime();
    return (activityQuery.data?.events ?? [])
      .filter((event) => new Date(event.createdAt).getTime() > baselineTime)
      .filter((event) => !seenRef.current.has(event.id))
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }, [activityQuery.data]);

  useEffect(() => {
    if (newEvents.length === 0) return;
    for (const event of newEvents) seenRef.current.add(event.id);

    const latest = newEvents.reduce((max, event) => {
      const time = new Date(event.createdAt).getTime();
      return time > max ? time : max;
    }, new Date(baselineRef.current).getTime());
    const nextBaseline = new Date(latest).toISOString();
    baselineRef.current = nextBaseline;
    try {
      localStorage.setItem(STORAGE_KEY, nextBaseline);
    } catch {
      /* ignore storage failures */
    }

    setToasts((current) => [
      ...newEvents.map((event) => ({ ...event, toastId: `${event.id}:${Date.now()}` })),
      ...current,
    ].slice(0, 4));

    void Promise.all([
      queryClient.invalidateQueries({ queryKey: adminKeys.overview() }),
      queryClient.invalidateQueries({ queryKey: adminKeys.retreats() }),
      queryClient.invalidateQueries({ queryKey: adminKeys.registrationsRoot() }),
      queryClient.invalidateQueries({ queryKey: adminKeys.sales() }),
    ]);
  }, [newEvents, queryClient]);

  useEffect(() => {
    if (toasts.length === 0) return;
    const timers = toasts.map((toast) =>
      window.setTimeout(() => {
        setToasts((current) => current.filter((item) => item.toastId !== toast.toastId));
      }, 9000),
    );
    return () => timers.forEach(window.clearTimeout);
  }, [toasts]);

  if (toasts.length === 0) return null;

  return (
    <div className="admin-toast-stack" aria-live="polite" aria-atomic="false">
      <style>{toastCss}</style>
      {toasts.map((toast) => (
        <Link
          key={toast.toastId}
          to={toast.href}
          className="admin-toast"
          onClick={() => setToasts((current) => current.filter((item) => item.toastId !== toast.toastId))}
        >
          <span className="admin-toast-initials">{toast.initials}</span>
          <span className="admin-toast-copy">
            <strong>{toastText(toast)}</strong>
            <span>{formatMoney(toast.amountCents)}</span>
          </span>
          <button
            type="button"
            className="admin-toast-close"
            aria-label="Dismiss notification"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setToasts((current) => current.filter((item) => item.toastId !== toast.toastId));
            }}
          >
            x
          </button>
        </Link>
      ))}
    </div>
  );
}

const toastCss = `
  .admin-toast-stack {
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 900;
    display: grid;
    gap: 10px;
    width: min(360px, calc(100vw - 32px));
  }
  .admin-toast {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 12px;
    padding: 12px 12px;
    border: 1px solid rgba(201,147,76,0.35);
    border-radius: 14px;
    background: #fff;
    box-shadow: 0 18px 42px -26px rgba(8,45,61,0.65);
    color: ${colors.navy};
    text-decoration: none;
    animation: adminToastIn 180ms ease-out;
  }
  .admin-toast-initials {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 999px;
    background: ${colors.berryRose};
    color: #fff;
    font-family: ${fonts.body};
    font-weight: 800;
    font-size: 12px;
    letter-spacing: 0.08em;
  }
  .admin-toast-copy {
    min-width: 0;
    display: grid;
    gap: 2px;
    font-family: ${fonts.body};
  }
  .admin-toast-copy strong {
    font-size: 13px;
    line-height: 1.25;
  }
  .admin-toast-copy span {
    font-size: 12px;
    color: rgba(8,45,61,0.58);
    font-weight: 700;
  }
  .admin-toast-close {
    border: 0;
    background: transparent;
    color: rgba(8,45,61,0.45);
    cursor: pointer;
    font-family: ${fonts.body};
    font-weight: 800;
    font-size: 13px;
  }
  .admin-toast-close:hover {
    color: ${colors.berryRose};
  }
  @keyframes adminToastIn {
    from { opacity: 0; transform: translateY(10px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
`;
